import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Gallery from './pages/Gallery';
import Chat from './pages/Chat';
import Entities from './pages/Entities';
import Places from './pages/Places';
import Review from './pages/Review';
import RankingDashboard from './pages/RankingDashboard';
import MyCollections from './pages/MyCollections';
import CollectionDetail from './pages/CollectionDetail';
import Settings from './pages/Settings';
import Callback from './pages/Callback';
import SpotifyCallback from './pages/SpotifyCallback';
import Login from './pages/Login';
import Privacy from './pages/Privacy';
import { AppSkeleton } from './components/Skeleton';

export function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading, login } = useAuth();

  if (isLoading) {
    return <AppSkeleton />;
  }

  if (!isAuthenticated) {
    // Preserve the full deep link (query string + hash), not just the bare
    // pathname — COL-87's URL state (?category=&q=&item=, /chat/:sessionId)
    // lives in `search`, and dropping it here is what stranded a logged-out
    // deep link on the default page after login (COL-154).
    login(`${window.location.pathname}${window.location.search}${window.location.hash}`);
    return <AppSkeleton />;
  }

  return children;
}

/**
 * Gates admin-only routes (/review, /rankings). This is a UX affordance, not
 * a security control — the bundle is public, so the real enforcement lives
 * server-side (require_admin() in lambdas/api/handler.py). This just keeps
 * non-admins from landing on a page that will only 403 underneath them.
 *
 * user.isAdmin comes from the signed Cognito id token's `cognito:groups`
 * claim (see AuthContext.checkAuth) — never client-settable.
 *
 * Composes ProtectedRoute rather than re-deriving its isLoading/isAuthenticated
 * handling, so both routes share one loading-skeleton/login-redirect
 * implementation and can't drift out of sync. A non-admin hitting the URL
 * directly is redirected to the collection view with no distinguishing
 * message, so the response doesn't reveal whether the route exists.
 */
export function AdminRoute({ children }) {
  const { user } = useAuth();

  return (
    <ProtectedRoute>
      {user?.isAdmin ? children : <Navigate to="/" replace />}
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/auth/callback" element={<Callback />} />
      <Route path="/login" element={<Login />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Gallery />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat/:sessionId"
        element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/review"
        element={
          <AdminRoute>
            <Review />
          </AdminRoute>
        }
      />
      <Route
        path="/rankings"
        element={
          <AdminRoute>
            <RankingDashboard />
          </AdminRoute>
        }
      />
      <Route
        path="/entities"
        element={
          <ProtectedRoute>
            <Entities />
          </ProtectedRoute>
        }
      />
      <Route
        path="/places"
        element={
          <ProtectedRoute>
            <Places />
          </ProtectedRoute>
        }
      />
      <Route
        path="/collections"
        element={
          <ProtectedRoute>
            <MyCollections />
          </ProtectedRoute>
        }
      />
      <Route
        path="/collections/:id"
        element={
          <ProtectedRoute>
            <CollectionDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/auth/spotify/callback"
        element={
          <ProtectedRoute>
            <SpotifyCallback />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
